import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Inbox, MessageSquare, Trash2, Mail, Phone, CheckCircle, XCircle } from 'lucide-react';
import './CahierDesCharges.css';

function CahierDesCharges({ currentUser }) {
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  const statuts = [
    { value: 'tous', label: 'Tous', color: '#6b7280' },
    { value: 'nouveau', label: 'Nouveau', color: '#3b82f6' },
    { value: 'contacte', label: 'Contacté', color: '#f59e0b' },
    { value: 'entretien', label: 'Entretien', color: '#8b5cf6' },
    { value: 'accepte', label: 'Accepté', color: '#10b981' },
    { value: 'refuse', label: 'Refusé', color: '#ef4444' }
  ];

  useEffect(() => {
    fetchCandidatures();
    fetchStats();
  }, [filterStatut, searchTerm]);

  const fetchCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatut !== 'tous') params.append('statut', filterStatut);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`/api/candidatures?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidatures(response.data);
    } catch (error) {
      console.error('Erreur chargement candidatures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/candidatures-stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const handleUpdateStatut = async (id, newStatut) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/candidatures/${id}`, { statut: newStatut }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCandidatures();
      fetchStats();
      if (selectedCandidature && selectedCandidature.id === id) {
        setSelectedCandidature({ ...selectedCandidature, statut: newStatut });
      }
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateNotes = async (id, notes) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/candidatures/${id}`, { notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedCandidature && selectedCandidature.id === id) {
        setSelectedCandidature({ ...selectedCandidature, notes });
      }
    } catch (error) {
      console.error('Erreur mise à jour notes:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette candidature ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/candidatures/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCandidatures();
      fetchStats();
      setShowDetailModal(false);
      setSelectedCandidature(null);
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatutBadge = (statut) => {
    const statutInfo = statuts.find(s => s.value === statut) || statuts[1];
    return (
      <span
        className="statut-badge"
        style={{ backgroundColor: statutInfo.color }}
      >
        {statutInfo.label}
      </span>
    );
  };

  const openDetail = (candidature) => {
    setSelectedCandidature(candidature);
    setShowDetailModal(true);
  };

  return (
    <div className="candidatures-inbox">
      {/* Header */}
      <div className="inbox-header">
        <div className="inbox-title">
          <h1>Boîte de réception</h1>
          <p className="inbox-subtitle">Candidatures reçues via le formulaire</p>
        </div>
        <a
          href="/cahier-des-charges"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-view-form"
        >
          Voir le formulaire public
        </a>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="inbox-stats">
          <div className="stat-card stat-total">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card stat-nouveau">
            <div className="stat-number">{stats.nouveau}</div>
            <div className="stat-label">Nouveaux</div>
          </div>
          <div className="stat-card stat-contacte">
            <div className="stat-number">{stats.contacte}</div>
            <div className="stat-label">Contactés</div>
          </div>
          <div className="stat-card stat-entretien">
            <div className="stat-number">{stats.entretien}</div>
            <div className="stat-label">Entretiens</div>
          </div>
          <div className="stat-card stat-accepte">
            <div className="stat-number">{stats.accepte}</div>
            <div className="stat-label">Acceptés</div>
          </div>
          <div className="stat-card stat-refuse">
            <div className="stat-number">{stats.refuse}</div>
            <div className="stat-label">Refusés</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="inbox-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Rechercher par nom, email, téléphone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-buttons">
          {statuts.map(s => (
            <button
              key={s.value}
              className={`filter-btn ${filterStatut === s.value ? 'active' : ''}`}
              onClick={() => setFilterStatut(s.value)}
              style={filterStatut === s.value ? { backgroundColor: s.color } : {}}
            >
              {s.label}
              {stats && s.value !== 'tous' && (
                <span className="filter-count">{stats[s.value] || 0}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="inbox-table-container">
        {loading ? (
          <div className="loading-state">Chargement...</div>
        ) : candidatures.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Inbox size={48} /></div>
            <h3>Aucune candidature</h3>
            <p>Les candidatures apparaîtront ici dès qu'un visiteur remplira le formulaire.</p>
          </div>
        ) : (
          <table className="inbox-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidatures.map(c => (
                <tr
                  key={c.id}
                  className={c.statut === 'nouveau' ? 'row-new' : ''}
                  onClick={() => openDetail(c)}
                >
                  <td className="col-date">{formatDate(c.created_at)}</td>
                  <td className="col-nom">
                    <strong>{c.nom}</strong>
                    {c.message && <span className="has-message" title="A un message"><MessageSquare size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /></span>}
                  </td>
                  <td className="col-email">{c.email}</td>
                  <td className="col-tel">{c.telephone || '-'}</td>
                  <td className="col-statut">{getStatutBadge(c.statut)}</td>
                  <td className="col-actions" onClick={e => e.stopPropagation()}>
                    <select
                      value={c.statut}
                      onChange={(e) => handleUpdateStatut(c.id, e.target.value)}
                      disabled={updating}
                      className="quick-status-select"
                    >
                      {statuts.filter(s => s.value !== 'tous').map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    <button
                      className="btn-delete-small"
                      onClick={() => handleDelete(c.id)}
                      title="Supprimer"
                    >
                      <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCandidature && (
        <div className="modal-overlay">
          <div className="modal-content detail-modal">
            <button className="modal-close" onClick={() => setShowDetailModal(false)}>×</button>

            <div className="detail-header">
              <h2>{selectedCandidature.nom}</h2>
              {getStatutBadge(selectedCandidature.statut)}
            </div>

            <div className="detail-info">
              <div className="info-row">
                <span className="info-label">Email</span>
                <a href={`mailto:${selectedCandidature.email}`} className="info-value link">
                  {selectedCandidature.email}
                </a>
              </div>
              <div className="info-row">
                <span className="info-label">Téléphone</span>
                {selectedCandidature.telephone ? (
                  <a href={`tel:${selectedCandidature.telephone}`} className="info-value link">
                    {selectedCandidature.telephone}
                  </a>
                ) : (
                  <span className="info-value">Non renseigné</span>
                )}
              </div>
              <div className="info-row">
                <span className="info-label">Date de candidature</span>
                <span className="info-value">{formatDate(selectedCandidature.created_at)}</span>
              </div>
            </div>

            {selectedCandidature.message && (
              <div className="detail-message">
                <h4>Message</h4>
                <p>{selectedCandidature.message}</p>
              </div>
            )}

            <div className="detail-statut">
              <h4>Changer le statut</h4>
              <div className="statut-buttons">
                {statuts.filter(s => s.value !== 'tous').map(s => (
                  <button
                    key={s.value}
                    className={`statut-btn ${selectedCandidature.statut === s.value ? 'active' : ''}`}
                    style={selectedCandidature.statut === s.value ? { backgroundColor: s.color } : { borderColor: s.color, color: s.color }}
                    onClick={() => handleUpdateStatut(selectedCandidature.id, s.value)}
                    disabled={updating}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="detail-notes">
              <h4>Notes internes</h4>
              <textarea
                placeholder="Ajouter des notes sur ce candidat..."
                value={selectedCandidature.notes || ''}
                onChange={(e) => setSelectedCandidature({ ...selectedCandidature, notes: e.target.value })}
                onBlur={(e) => handleUpdateNotes(selectedCandidature.id, e.target.value)}
                rows="4"
              />
            </div>

            <div className="detail-actions">
              <a
                href={`mailto:${selectedCandidature.email}`}
                className="btn-action btn-email"
              >
                <Mail size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer un email
              </a>
              {selectedCandidature.telephone && (
                <a
                  href={`tel:${selectedCandidature.telephone}`}
                  className="btn-action btn-call"
                >
                  <Phone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Appeler
                </a>
              )}
              <button
                className="btn-action btn-delete"
                onClick={() => handleDelete(selectedCandidature.id)}
              >
                <Trash2 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CahierDesCharges;
